import { MouseEvent } from "react";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import StatusDot from "../shared/StatusDot";
import {
  CATALOGUE_CELL_PADDING,
  STRIPE_BACKGROUND,
} from "../shared/constants";
import PriceBookTypeChip from "./TypeChip";
import {
  DRAFT_LABEL,
  formatBranchReach,
  formatDiscountPercent,
} from "./constants";
import { buildStoreBlockerMessage } from "./deleteConflict";
import { formatDateTimeBkk, todayInBangkok } from "../../../utils/date";
import { numberWithCommas } from "../../../utils";
import { formatExpiringCountLabel } from "./Periods/periodModel";
import { formatScheduleChipLabel } from "./Schedules/scheduleModel";
import { formatValidityChipLabel } from "./validityModel";
import { PriceBookRow as PriceBookRowData } from "./types";

const EMPTY_PLACEHOLDER = "-";

/** A draft is only being tried in 00-HO, so its row reads fainter than the live books. */
const DRAFT_ROW_OPACITY = 0.6;

interface PriceBookRowProps {
  priceBook: PriceBookRowData;
  /** Kept identical to the loading skeleton's rows so the table never resizes on load. */
  rowHeight: number;
  striped: boolean;
  canEdit: boolean;
  onView: (uuid: string) => void;
  onOpenCustomers: (uuid: string) => void;
  onDuplicate: (uuid: string) => void;
  onDelete: (priceBook: PriceBookRowData) => void;
}

export default function PriceBookTableRow({
  priceBook,
  rowHeight,
  striped,
  canEdit,
  onView,
  onOpenCustomers,
  onDuplicate,
  onDelete,
}: PriceBookRowProps) {
  // The API refuses to delete a pricebook any store still uses, so the button is disabled
  // with the way out spelled out rather than letting the user walk into the refusal. The
  // products inside a pricebook are deleted with it and never block.
  const hasStores = priceBook.activeStoreCount > 0;
  const hasExpiringPeriods = priceBook.expiringPeriodCount > 0;
  const scheduleChipLabel = priceBook.nextScheduleDate
    ? formatScheduleChipLabel(priceBook.nextScheduleDate)
    : "";
  // Only a book that does not price today is flagged; a running validity needs no chip.
  const validityChipLabel = formatValidityChipLabel(priceBook.validity, todayInBangkok());

  const deleteHint = hasStores
    ? buildStoreBlockerMessage(priceBook.activeStoreCount)
    : "ลบสมุดราคา";
  const storeCountLabel = `${numberWithCommas(priceBook.activeStoreCount)} ราย`;
  const branchReachLabel = formatBranchReach(
    priceBook.branchScope,
    priceBook.branchCount,
  );

  const handleView = () => onView(priceBook.uuid);

  const handleOpenCustomers = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onOpenCustomers(priceBook.uuid);
  };

  // Prices, branches and the header are all edited on the detail page.
  const handleEdit = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onView(priceBook.uuid);
  };

  const handleDuplicate = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDuplicate(priceBook.uuid);
  };

  const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete(priceBook);
  };

  const handleViewFromButton = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onView(priceBook.uuid);
  };

  return (
    <TableRow
      sx={{
        bgcolor: striped ? STRIPE_BACKGROUND : "#fff",
        height: rowHeight,
        "& td, & th": { padding: `${CATALOGUE_CELL_PADDING}px` },
        "& td": { opacity: priceBook.isDraft ? DRAFT_ROW_OPACITY : 1 },
        cursor: "pointer",
      }}
      onClick={handleView}
    >
      <TableCell align="center">{priceBook.no}</TableCell>
      <TableCell align="center">{priceBook.priceBookCode}</TableCell>
      {/* The only left-aligned column: names are the one free-length value here, and
          centring them would ragged both of their edges. */}
      <TableCell>
        <Stack direction="row" alignItems="center" gap={1}>
          {priceBook.priceBookName}
          {priceBook.isDraft && (
            <Chip label={DRAFT_LABEL} size="small" color="warning" />
          )}
          {hasExpiringPeriods && (
            <Chip
              label={formatExpiringCountLabel(priceBook.expiringPeriodCount)}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
          {scheduleChipLabel && (
            <Chip label={scheduleChipLabel} size="small" color="info" variant="outlined" />
          )}
          {validityChipLabel && (
            <Chip label={validityChipLabel} size="small" variant="outlined" />
          )}
        </Stack>
      </TableCell>
      <TableCell align="center">
        <PriceBookTypeChip type={priceBook.priceBookType} fullWidth />
      </TableCell>
      <TableCell align="center">
        {formatDiscountPercent(priceBook.discountPercent)}
      </TableCell>
      <TableCell align="center">{branchReachLabel}</TableCell>
      <TableCell align="center">
        {numberWithCommas(priceBook.productCount)}
      </TableCell>
      <TableCell align="center">
        {hasStores ? (
          <Link
            component="button"
            underline="hover"
            onClick={handleOpenCustomers}
          >
            {storeCountLabel}
          </Link>
        ) : (
          storeCountLabel
        )}
      </TableCell>
      <TableCell>
        <Stack direction="row" justifyContent="center">
          <StatusDot status={priceBook.priceBookStatus} />
        </Stack>
      </TableCell>
      {/* The list filters created_from / created_to as Bangkok business days, so both
          timestamps are read on that clock rather than on the viewer's. */}
      <TableCell align="center">
        {formatDateTimeBkk(priceBook.createdAt) || EMPTY_PLACEHOLDER}
      </TableCell>
      <TableCell align="center">
        {formatDateTimeBkk(priceBook.updatedAt) || EMPTY_PLACEHOLDER}
      </TableCell>
      <TableCell align="center">
        {priceBook.updatedByName || EMPTY_PLACEHOLDER}
      </TableCell>
      <TableCell>
        <Stack direction="row" gap={0.5} justifyContent="center">
          <Tooltip title="ดูรายละเอียด">
            <IconButton
              size="small"
              color="primary"
              onClick={handleViewFromButton}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Tooltip title="แก้ไขสมุดราคา">
              <IconButton size="small" color="primary" onClick={handleEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title="ทำสำเนาเป็นเล่มใหม่">
              <IconButton
                size="small"
                color="primary"
                onClick={handleDuplicate}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title={deleteHint}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={hasStores}
                  onClick={handleDelete}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
